<script setup>
import { computed } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  option: {
    type: Object,
    default: () => {},
  },
  variant: {
    type: String,
    default: 'default',
  },
});

const hasLeadingVisual = computed(
  () =>
    (props.variant === 'icon' && props.option.icon) ||
    (props.variant === 'label' && props.option.color) ||
    props.variant === 'agent'
);

const toneClass = computed(() =>
  props.option?.tone ? `menu-item--${props.option.tone}` : ''
);
</script>

<template>
  <div
    class="menu-item min-w-0"
    :class="[toneClass, { 'menu-item--no-leading': !hasLeadingVisual }]"
    role="button"
  >
    <div v-if="hasLeadingVisual" class="menu-item__leading">
      <fluent-icon
        v-if="variant === 'icon' && option.icon"
        :icon="option.icon"
        size="14"
        class="flex-shrink-0"
      />
      <span
        v-if="variant === 'label' && option.color"
        class="label-pill flex-shrink-0"
        :style="{ backgroundColor: option.color }"
      />
      <Avatar
        v-if="variant === 'agent'"
        :name="option.label"
        :src="option.thumbnail"
        :status="option.status === 'online' ? option.status : null"
        :size="20"
        class="flex-shrink-0"
      />
    </div>
    <p class="menu-item__label truncate min-w-0 flex-1">
      {{ option.label }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.menu-item {
  width: 100%;
  min-width: 14rem;
  gap: 0.65rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.875rem;
  color: var(--context-menu-text-primary) !important;
  transition:
    background-color 0.18s ease,
    color 0.18s ease,
    transform 0.18s ease,
    box-shadow 0.18s ease;
  @apply flex items-center flex-nowrap overflow-hidden cursor-pointer;

  &:hover {
    background: var(--context-menu-hover);
    color: var(--context-menu-hover-strong) !important;
    transform: translateX(2px);
  }

  &:hover .menu-item__leading {
    border-color: rgba(249, 115, 22, 0.2);
    background: rgba(249, 115, 22, 0.1);
    color: var(--context-menu-hover-strong) !important;
  }

  &:hover .menu-item__label {
    color: inherit !important;
  }
}

.menu-item--no-leading {
  padding-inline-start: 0.9rem;
}

.menu-item--danger:hover {
  background: var(--context-menu-danger-hover);
  color: var(--context-menu-danger-text) !important;
}

.menu-item--danger:hover .menu-item__leading {
  border-color: rgba(239, 68, 68, 0.18);
  background: rgba(239, 68, 68, 0.12);
  color: var(--context-menu-danger-text) !important;
}

.menu-item__leading {
  width: 1.75rem;
  height: 1.75rem;
  min-width: 1.75rem;
  border: 1px solid var(--context-menu-border-soft);
  border-radius: 0.75rem;
  background: var(--context-menu-surface-secondary);
  color: var(--context-menu-text-secondary) !important;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease;
  @apply inline-flex items-center justify-center;
}

.menu-item__label {
  margin: 0;
  color: var(--context-menu-text-primary) !important;
  font-size: var(--wlab-font-md);
  font-weight: 600;
  line-height: 1.2;
  transition: color 0.18s ease;
}

.agent-thumbnail {
  margin-top: 0 !important;
}

.label-pill {
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 9999px;
  border: 1px solid var(--context-menu-surface);
  @apply flex-shrink-0;
}
</style>
